interface SmsSignupProps {
  onRValueClick: (rValue: string) => void;
  activeRValue: string | null;
  inStockRValues: string[];
  inventoryLoading: boolean;
}

const ALL_R_VALUES = ["R-11","R-13","R-15","R-19","R-21","R-30","R-38"];

const R_VALUE_GROUPS = [
  { label: "2x4 Walls", values: ["R-11","R-13","R-15"] },
  { label: "2x6 Walls / Crawlspace", values: ["R-19","R-21"] },
  { label: "Attic / Crawlspace", values: ["R-30"] },
  { label: "Attic", values: ["R-38"] },
];

export default function SmsSignup({ onRValueClick, activeRValue, inStockRValues, inventoryLoading }: SmsSignupProps) {
  // Use live in-stock values if loaded, otherwise fall back to all known values
  const availableRValues = inStockRValues.length > 0 ? inStockRValues : (inventoryLoading ? [] : ALL_R_VALUES);
  return (
    <section
      className="relative w-full py-8 md:py-12 px-4 overflow-hidden"
      style={{
        width: "100vw",
        marginLeft: "calc(-50vw + 50%)",
      }}
      style={{
        background: "linear-gradient(135deg, #0a1628 0%, #0d1f3c 40%, #1a0a0a 70%, #2a0000 100%)",
      }}
    >
      {/* Stars background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(40)].map((_, i) => (
          <span
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              width: i % 3 === 0 ? "3px" : "2px",
              height: i % 3 === 0 ? "3px" : "2px",
              top: `${(i * 37 + 11) % 100}%`,
              left: `${(i * 53 + 7) % 55}%`,
              opacity: 0.4 + (i % 5) * 0.1,
            }}
          />
        ))}
      </div>

      {/* Red & white stripes accent — right side */}
      <div className="absolute top-0 right-0 h-full w-24 md:w-40 overflow-hidden pointer-events-none opacity-20">
        {[...Array(7)].map((_, i) => (
          <div
            key={i}
            className="absolute w-full"
            style={{
              top: `${i * (100 / 7)}%`,
              height: `${100 / 7}%`,
              background: i % 2 === 0 ? "#cc0000" : "#ffffff",
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative max-w-3xl mx-auto text-center">

        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
          <span className="text-lg">🇺🇸</span>
          <span className="text-white/80 text-xs font-black uppercase tracking-widest">American Owned &amp; Operated</span>
        </div>

        {/* Headline */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight mb-3 tracking-tight">
          Cheapest Insulation
          <span className="block text-red-400">In Town.</span>
        </h1>

        <p className="text-white/60 text-base md:text-lg font-semibold mb-10">
          Guaranteed lowest prices on fiberglass insulation &nbsp;·&nbsp; Buford, GA
        </p>

        {/* Big phone button */}
        <a
          href="tel:678-936-5391"
          className="inline-flex items-center gap-4 bg-red-600 hover:bg-red-500 active:bg-red-700 text-white font-black text-2xl md:text-3xl px-10 md:px-14 py-6 md:py-7 rounded-2xl transition-colors cursor-pointer whitespace-nowrap"
          style={{ letterSpacing: "0.02em" }}
        >
          <i className="ri-phone-line text-3xl md:text-4xl"></i>
          678-936-5391
        </a>

        <p className="text-white/30 text-xs font-bold uppercase tracking-widest mt-6">
          Call or Text &nbsp;·&nbsp; Pickup by Appointment Only
        </p>

        <a
          href="https://share.google/BAnWlIgXndMB6xxll"
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="inline-flex items-center gap-2 mt-5 text-white/50 hover:text-white/90 text-xs font-bold uppercase tracking-widest transition-colors cursor-pointer"
        >
          <i className="ri-map-pin-2-line text-red-400 text-sm"></i>
          View Us on Google Maps
        </a>

        {/* Divider */}
        <div className="border-t border-white/10 mt-10 pt-10">

          {/* R-Values — moved to top so it's first thing seen on mobile */}
          <div className="mb-4">
            <p className="text-white text-lg md:text-xl font-black uppercase tracking-wide mb-1">
              Tap an R-Value to See What&apos;s In Stock
            </p>
            <p className="text-white/40 text-xs font-semibold">
              Click any R-value below to jump straight to that inventory
            </p>
          </div>
          {inventoryLoading ? (
            /* Skeleton while loading */
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="h-9 w-16 rounded-full bg-white/10 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {R_VALUE_GROUPS.map((group) => {
                const visibleValues = group.values.filter((r) => availableRValues.includes(r));
                if (visibleValues.length === 0) return null;
                return (
                  <div key={group.label} className="flex flex-col items-center gap-2">
                    <span className="text-white/50 text-[10px] font-black uppercase tracking-widest border border-white/20 rounded-full px-3 py-0.5 whitespace-nowrap">
                      {group.label}
                    </span>
                    <div className="flex gap-2">
                      {visibleValues.map((r) => (
                        <button
                          key={r}
                          onClick={() => onRValueClick(r)}
                          className={`border-2 text-sm font-black px-5 py-2 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap
                            ${activeRValue === r
                              ? "bg-red-600 border-red-500 text-white scale-110"
                              : "bg-white/10 border-white/30 text-white hover:bg-red-600 hover:border-red-500 hover:scale-110"
                            }`}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Description */}
          <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-8">
            We carry brand new batts and rolls fiberglass insulation in a wide range of R-values
            to suit different insulation needs for homes and buildings. Whether you&apos;re insulating
            walls, attics, crawlspaces, or floors — we&apos;ve got you covered at the lowest prices.
          </p>

          {/* Widths */}
          <div className="flex justify-center gap-4">
            {[
              { value: '16"', label: "Width" },
              { value: '24"', label: "Width" },
              { value: "Appt", label: "Only" },
            ].map((item) => (
              <div key={item.value} className="bg-white/10 border border-white/20 rounded-lg px-6 py-3 text-center">
                <p className="text-white font-black text-lg">{item.value}</p>
                <p className="text-white/40 text-xs uppercase tracking-wider">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
